// BRE-Loyalty contracts (docs/contracts/bre-loyalty.md).
//
// Loyalty is authoritative for balances, tiers, reward eligibility and points
// math. Ticketing only asks, reserves, commits, releases, and reports facts.
import { z } from 'zod';
import { eventEnvelope } from '../events';
import { BreAuthSub, Id, NonNegativeCents, Timestamp } from '../primitives';

// ---------------------------------------------------------------------------
// EXISTING synchronous endpoints (api.loyalty.bre.live), called with
// Authorization: Bearer ${TICKETING_LOYALTY_API_SECRET} once bre-loyalty
// accepts a purpose-scoped secret restricted to these routes.
// ---------------------------------------------------------------------------

/** GET /customer-summary/:sub (subset Ticketing renders; extra fields pass through). */
export const LoyaltyCustomerSummary = z.looseObject({
  customer_id: BreAuthSub,
  points_balance: z.int(),
  tier_name: z.string().nullable(),
  lifetime_spend_cents: z.int().optional(),
  order_count: z.int().optional(),
});
export type LoyaltyCustomerSummary = z.infer<typeof LoyaltyCustomerSummary>;

/** GET /rewards?customer_id= */
export const LoyaltyRewardsResponse = z.object({
  rewards: z.array(
    z.looseObject({
      id: z.union([z.string(), z.int()]),
      name: z.string(),
      points_cost: z.int(),
      discount_cents: NonNegativeCents,
      reward_kind: z.string(),
      affordable: z.boolean(),
      is_staged: z.boolean().optional(),
    }),
  ),
});

/** GET /rewards/:id/verify?customer_id= */
export const LoyaltyRewardVerifyResponse = z.discriminatedUnion('valid', [
  z.object({ valid: z.literal(true), points_cost: z.int(), discount_cents: NonNegativeCents }),
  z.object({ valid: z.literal(false), error: z.string() }),
]);

/** POST /rewards/:id/reserve */
export const LoyaltyRewardReserveRequest = z.object({
  customer_id: BreAuthSub,
  plan_id: z.string(),
  ttl_seconds: z.int().min(60).max(900),
});
export const LoyaltyRewardReserveResponse = z.object({
  reservation_id: z.string(),
  expires_at: z.string(),
  points_cost: z.int(),
});

/**
 * GET /orders/points?source_service=&order_ref= — **proposed** (D-78).
 *
 * What loyalty posted for one Ticketing order, so the storefront can show the
 * customer what they earned and spent without Ticketing computing either. 404
 * means nothing has been posted for that order yet.
 */
export const LoyaltyOrderPointsResponse = z.looseObject({
  order_ref: z.string(),
  points_earned: z.int(),
  points_spent: z.int(),
  points_balance: z.int().optional(),
  posted_at: Timestamp.nullish(),
});
export type LoyaltyOrderPointsResponse = z.infer<typeof LoyaltyOrderPointsResponse>;

/** POST /rewards/reservations/:id/commit-reservation */
export const LoyaltyRewardCommitResponse = z.object({ ok: z.literal(true), already_committed: z.boolean() });

/** POST /rewards/reservations/:id/release-reservation */
export const LoyaltyRewardReleaseResponse = z.object({ ok: z.literal(true), released: z.boolean() });

// ---------------------------------------------------------------------------
// PROPOSED queue contract: bre-ticketing-loyalty-events, consumed by
// bre-loyalty-api. Order references are Ticketing UUIDs namespaced by
// source_service — never the integer order_id space POS uses — because
// bre-loyalty's earn idempotency is keyed on order id.
//
// Ticketing sends the full money breakdown; bre-loyalty decides what is
// points-eligible. Ticketing never computes points or eligibility.
// ---------------------------------------------------------------------------
const SourceService = z.literal('bre-ticketing');

const MoneyBreakdown = z.object({
  face_value_cents: NonNegativeCents,
  discount_cents: NonNegativeCents,
  comp_cents: NonNegativeCents,
  fees_cents: NonNegativeCents,
  tax_cents: NonNegativeCents,
  customer_total_cents: NonNegativeCents,
  card_and_cash_tender_cents: NonNegativeCents,
  gift_card_tender_cents: NonNegativeCents,
  loyalty_reward_tender_cents: NonNegativeCents,
});

export const LoyaltyTicketOrderCompletedV1 = eventEnvelope(
  'ticketing.order_completed',
  1,
  z.object({
    source_service: SourceService,
    order_ref: Id,
    order_number: z.string(),
    customer_sub: BreAuthSub.nullable(),
    completed_at: Timestamp,
    amounts: MoneyBreakdown,
    items: z.array(
      z.object({
        event_title: z.string(),
        performance_starts_at: Timestamp,
        ticket_type_name: z.string(),
        quantity: z.int().min(1),
        face_value_cents: NonNegativeCents,
      }),
    ),
    /** Donations in the order: activity for the profile, never an earn (D-51). */
    donation_cents: NonNegativeCents.optional(),
  }),
);

/** A gift was paid (D-51): customer activity that earns nothing. */
export const LoyaltyTicketDonationCompletedV1 = eventEnvelope(
  'ticketing.donation_completed',
  1,
  z.object({
    source_service: SourceService,
    donation_ref: Id,
    campaign_id: Id,
    kind: z.enum(['ONE_TIME', 'RECURRING', 'CHECKOUT', 'REFUND_CONVERSION']),
    first_payment: z.boolean(),
    amount_cents: NonNegativeCents,
    customer_sub: BreAuthSub.nullable(),
    donor_email: z.string().nullable(),
    earns_points: z.literal(false),
    paid_at: Timestamp,
  }),
);

export const LoyaltyTicketOrderCustomerAttachedV1 = eventEnvelope(
  'ticketing.order_customer_attached',
  1,
  z.object({ source_service: SourceService, order_ref: Id, customer_sub: BreAuthSub, attached_at: Timestamp }),
);

export const LoyaltyTicketOrderRefundedV1 = eventEnvelope(
  'ticketing.order_refunded',
  1,
  z.object({
    source_service: SourceService,
    order_ref: Id,
    refund_ref: Id,
    customer_sub: BreAuthSub.nullable(),
    refunded_at: Timestamp,
    amounts: MoneyBreakdown,
  }),
);

export const LoyaltyRewardAppliedV1 = eventEnvelope(
  'ticketing.reward_applied',
  1,
  z.object({
    source_service: SourceService,
    order_ref: Id,
    customer_sub: BreAuthSub,
    reward_id: z.string(),
    reservation_id: z.string(),
    tender_cents: NonNegativeCents,
    /** Loyalty's own points figure, as it was quoted when the reward was reserved. */
    points_cost: z.int().min(0).optional(),
  }),
);
